import type {
  ConfigEntity,
  ConfigEntityConfiguration,
  DomainEntity,
} from '../types/configuration';
import { getConfigEntitySchemaWithDomainEntityAttributes } from './get-config-entity-schema-with-domain-entity-attributes/get-config-entity-schema-with-domain-entity-attributes';
import { getConfigEntitySchemaWithDomainEntityAttributesAndRelationships } from './get-config-entity-schema-with-domain-entity-attributes-and-relationships/get-config-entity-schema-with-domain-entity-attributes-and-relationships';

/**
 * The config app backend uses a generic schema, stored in the configEntity.schema property. Domain-specific
 * attributes are stored in `attributes.domainEntityAttributes`, a generic object at the configEntity level.
 * However, each domain entity implements its own schema and we need to use that for form generation and validation of the config-entity
 * as a whole for any given domain entity.
 *
 * This function combines the two, inserting the domain entity's `attributes` schema into the config entity's schema
 * in the `domainEntityAttributes` slot. Endpoints are sourced from the domain entity itself — they're pre-filtered
 * at tenant-config build time (see `buildEntityConfigs`) for CACT-BE entities, or shipped
 * inline in the registry yaml for entities with `isUsingConfigAppBackend: false`.
 *
 * @param configEntity {ConfigEntityConfiguration}
 * @param domainEntity {DomainEntity}
 * @returns {@link ConfigEntity}
 */

export const getConfigEntityWithDomainEntitySchema = (
  configEntity: ConfigEntityConfiguration,
  domainEntity: DomainEntity,
  includeRelationships: boolean = false,
): ConfigEntity => ({
  ...configEntity,
  endpoints: domainEntity.endpoints,
  domainEntity,
  schema: includeRelationships
    ? getConfigEntitySchemaWithDomainEntityAttributesAndRelationships(
        configEntity.schema,
        domainEntity.schema,
      )
    : getConfigEntitySchemaWithDomainEntityAttributes(
        configEntity.schema,
        domainEntity.schema,
      ),
});
